import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, CogIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useMutation } from '../../hooks/useApi';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient from '../../utils/api';

interface GrowthRateConfig {
  id: string;
  species: string;
  stage: string;
  growth_rate_g_per_week: number;
  growth_rate_g_per_month: number;
  min_harvest_weight_g: number;
  optimal_harvest_weight_g: number;
  optimistic_multiplier: number;
  pessimistic_multiplier: number;
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface SeasonConfig {
  id: string;
  season_name: string;
  start_month: number;
  end_month: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface GrowthRateFormData {
  species: string;
  stage: string;
  growth_rate_g_per_week: number;
  min_harvest_weight_g: number;
  optimal_harvest_weight_g: number;
  optimistic_multiplier: number;
  pessimistic_multiplier: number;
  notes: string;
  is_active: boolean;
}

interface SeasonFormData {
  season_name: string;
  start_month: number;
  end_month: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  notes: string;
}

const GrowthSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'growth' | 'seasons'>('growth');
  const [growthRates, setGrowthRates] = useState<GrowthRateConfig[]>([]);
  const [seasons, setSeasons] = useState<SeasonConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showGrowthForm, setShowGrowthForm] = useState(false);
  const [showSeasonForm, setShowSeasonForm] = useState(false);
  const [editingGrowthRate, setEditingGrowthRate] = useState<GrowthRateConfig | null>(null);
  const [editingSeason, setEditingSeason] = useState<SeasonConfig | null>(null);

  const { mutate: createGrowthRate, loading: creatingGrowth } = useMutation<GrowthRateConfig, GrowthRateFormData>();
  const { mutate: updateGrowthRate, loading: updatingGrowth } = useMutation<GrowthRateConfig, GrowthRateFormData>();
  const { mutate: deleteGrowthRate, loading: deletingGrowth } = useMutation();
  const { mutate: createSeason, loading: creatingSeason } = useMutation<SeasonConfig, SeasonFormData>();
  const { mutate: updateSeason, loading: updatingSeason } = useMutation<SeasonConfig, SeasonFormData>();
  const { mutate: deleteSeason, loading: deletingSeason } = useMutation();

  const fetchGrowthRates = async () => {
    try {
      const response = await apiClient.get('/growth-rate-configs');
      setGrowthRates(response.data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const fetchSeasons = async () => {
    try {
      const response = await apiClient.get('/season-configs');
      setSeasons(response.data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchGrowthRates(), fetchSeasons()])
      .finally(() => setLoading(false));
  }, []);

  const handleCreateGrowthRate = async (data: GrowthRateFormData) => {
    try {
      await createGrowthRate('/growth-rate-configs', data);
      await fetchGrowthRates();
      setShowGrowthForm(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateGrowthRate = async (data: GrowthRateFormData) => {
    if (!editingGrowthRate) return;
    try {
      await updateGrowthRate(`/growth-rate-configs/${editingGrowthRate.id}`, data, 'PUT');
      await fetchGrowthRates();
      setEditingGrowthRate(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleGrowthRateStatus = async (id: string, newStatus: boolean) => {
    try {
      const rate = growthRates.find(r => r.id === id);
      if (!rate) return;
      
      await updateGrowthRate(`/growth-rate-configs/${id}`, {
        species: rate.species,
        stage: rate.stage,
        growth_rate_g_per_week: rate.growth_rate_g_per_week,
        min_harvest_weight_g: rate.min_harvest_weight_g,
        optimal_harvest_weight_g: rate.optimal_harvest_weight_g,
        optimistic_multiplier: rate.optimistic_multiplier,
        pessimistic_multiplier: rate.pessimistic_multiplier,
        notes: rate.notes || '',
        is_active: newStatus
      }, 'PUT');
      await fetchGrowthRates();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteGrowthRate = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this growth rate configuration?')) return;
    try {
      await deleteGrowthRate(`/growth-rate-configs/${id}`, undefined, 'DELETE');
      await fetchGrowthRates();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCreateSeason = async (data: SeasonFormData) => {
    try {
      await createSeason('/season-configs', data);
      await fetchSeasons();
      setShowSeasonForm(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateSeason = async (data: SeasonFormData) => {
    if (!editingSeason) return;
    try {
      await updateSeason(`/season-configs/${editingSeason.id}`, data, 'PUT');
      await fetchSeasons();
      setEditingSeason(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleSeasonStatus = async (id: string, newStatus: boolean) => {
    try {
      const season = seasons.find(s => s.id === id);
      if (!season) return;
      
      await updateSeason(`/season-configs/${id}`, {
        season_name: season.season_name,
        start_month: season.start_month,
        end_month: season.end_month,
        start_date: season.start_date,
        end_date: season.end_date,
        notes: season.notes || '',
        is_active: newStatus
      }, 'PUT');
      await fetchSeasons();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteSeason = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this season configuration?')) return;
    try {
      await deleteSeason(`/season-configs/${id}`, undefined, 'DELETE');
      await fetchSeasons();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getMonthName = (month: number) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Growth Settings</h1>
        <p className="text-gray-600">Configure growth rates and farming seasons</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex flex-wrap gap-4 sm:gap-8">
            <button
              onClick={() => setActiveTab('growth')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'growth'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CogIcon className="h-5 w-5 inline mr-2" />
              Growth Rates
            </button>
            <button
              onClick={() => setActiveTab('seasons')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'seasons'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CalendarIcon className="h-5 w-5 inline mr-2" />
              Seasons
            </button>
          </nav>
        </div>
      </div>

      {/* Growth Rates Tab */}
      {activeTab === 'growth' && (
        <div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Growth Rate Configurations</h2>
            <button
              onClick={() => setShowGrowthForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Add Growth Rate
            </button>
          </div>

          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Species</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Growth Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Harvest Weights</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Multipliers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {growthRates.map((rate) => (
                  <tr key={rate.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{rate.species}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        rate.stage === 'nursery' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {rate.stage}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{rate.growth_rate_g_per_week}g/week</div>
                      <div className="text-xs text-gray-500">({rate.growth_rate_g_per_month}g/month)</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">Min: {rate.min_harvest_weight_g}g</div>
                      <div className="text-sm text-gray-900">Optimal: {rate.optimal_harvest_weight_g}g</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">Optimistic: {rate.optimistic_multiplier}x</div>
                      <div className="text-sm text-gray-900">Pessimistic: {rate.pessimistic_multiplier}x</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        rate.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {rate.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setEditingGrowthRate(rate)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleGrowthRateStatus(rate.id, !rate.is_active)}
                          className={`p-1 rounded ${
                            rate.is_active 
                              ? 'text-green-600 hover:text-green-900 hover:bg-green-50' 
                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                          }`}
                          title={rate.is_active ? 'Deactivate' : 'Activate'}
                        >
                          <div className={`w-3 h-3 rounded-full ${rate.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                        </button>
                        <button
                          onClick={() => handleDeleteGrowthRate(rate.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                          disabled={deletingGrowth}
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* Seasons Tab */}
      {activeTab === 'seasons' && (
        <div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Season Configurations</h2>
            <button
              onClick={() => setShowSeasonForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Add Season
            </button>
          </div>

          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Season Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {seasons.map((season) => (
                  <tr key={season.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{season.season_name}</div>
                      {season.notes && (
                        <div className="text-xs text-gray-500">{season.notes}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getMonthName(season.start_month)} - {getMonthName(season.end_month)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(season.start_date).toLocaleDateString()} - {new Date(season.end_date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        season.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {season.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setEditingSeason(season)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleSeasonStatus(season.id, !season.is_active)}
                          className={`p-1 rounded ${
                            season.is_active 
                              ? 'text-green-600 hover:text-green-900 hover:bg-green-50' 
                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                          }`}
                          title={season.is_active ? 'Deactivate' : 'Activate'}
                        >
                          <div className={`w-3 h-3 rounded-full ${season.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                        </button>
                        <button
                          onClick={() => handleDeleteSeason(season.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                          disabled={deletingSeason}
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* Growth Rate Form Modal */}
      {showGrowthForm && (
        <GrowthRateForm
          onSubmit={handleCreateGrowthRate}
          onCancel={() => setShowGrowthForm(false)}
          loading={creatingGrowth}
        />
      )}

      {/* Edit Growth Rate Form Modal */}
      {editingGrowthRate && (
        <GrowthRateForm
          initialData={editingGrowthRate}
          onSubmit={handleUpdateGrowthRate}
          onCancel={() => setEditingGrowthRate(null)}
          loading={updatingGrowth}
        />
      )}

      {/* Season Form Modal */}
      {showSeasonForm && (
        <SeasonForm
          onSubmit={handleCreateSeason}
          onCancel={() => setShowSeasonForm(false)}
          loading={creatingSeason}
        />
      )}

      {/* Edit Season Form Modal */}
      {editingSeason && (
        <SeasonForm
          initialData={editingSeason}
          onSubmit={handleUpdateSeason}
          onCancel={() => setEditingSeason(null)}
          loading={updatingSeason}
        />
      )}
    </div>
  );
};

// Growth Rate Form Component
const GrowthRateForm: React.FC<{
  initialData?: GrowthRateConfig;
  onSubmit: (data: GrowthRateFormData) => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ initialData, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState<GrowthRateFormData>({
    species: initialData?.species || '',
    stage: initialData?.stage || 'nursery',
    growth_rate_g_per_week: initialData?.growth_rate_g_per_week || 0,
    min_harvest_weight_g: initialData?.min_harvest_weight_g || 0,
    optimal_harvest_weight_g: initialData?.optimal_harvest_weight_g || 0,
    optimistic_multiplier: initialData?.optimistic_multiplier || 1.2,
    pessimistic_multiplier: initialData?.pessimistic_multiplier || 0.8,
    notes: initialData?.notes || '',
    is_active: initialData?.is_active ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-6 sm:top-20 mx-auto p-4 sm:p-5 border w-[calc(100%-2rem)] max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {initialData ? 'Edit Growth Rate' : 'Add Growth Rate'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Species</label>
              <input
                type="text"
                value={formData.species}
                onChange={(e) => setFormData({ ...formData, species: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Stage</label>
              <select
                value={formData.stage}
                onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="nursery">Nursery</option>
                <option value="grower">Grower</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Growth Rate (g/week)</label>
              <input
                type="number"
                step="0.1"
                value={formData.growth_rate_g_per_week}
                onChange={(e) => setFormData({ ...formData, growth_rate_g_per_week: parseFloat(e.target.value) })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Min Harvest Weight (g)</label>
                <input
                  type="number"
                  value={formData.min_harvest_weight_g}
                  onChange={(e) => setFormData({ ...formData, min_harvest_weight_g: parseInt(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Optimal Harvest Weight (g)</label>
                <input
                  type="number"
                  value={formData.optimal_harvest_weight_g}
                  onChange={(e) => setFormData({ ...formData, optimal_harvest_weight_g: parseInt(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Optimistic Multiplier</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.optimistic_multiplier}
                  onChange={(e) => setFormData({ ...formData, optimistic_multiplier: parseFloat(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Pessimistic Multiplier</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.pessimistic_multiplier}
                  onChange={(e) => setFormData({ ...formData, pessimistic_multiplier: parseFloat(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows={3}
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">Active</label>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? <LoadingSpinner size="sm" /> : (initialData ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Season Form Component
const SeasonForm: React.FC<{
  initialData?: SeasonConfig;
  onSubmit: (data: SeasonFormData) => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ initialData, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState<SeasonFormData>({
    season_name: initialData?.season_name || '',
    start_month: initialData?.start_month || 1,
    end_month: initialData?.end_month || 12,
    start_date: initialData?.start_date || '',
    end_date: initialData?.end_date || '',
    is_active: initialData?.is_active ?? true,
    notes: initialData?.notes || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-6 sm:top-20 mx-auto p-4 sm:p-5 border w-[calc(100%-2rem)] max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {initialData ? 'Edit Season' : 'Add Season'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Season Name</label>
              <input
                type="text"
                value={formData.season_name}
                onChange={(e) => setFormData({ ...formData, season_name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Month</label>
                <select
                  value={formData.start_month}
                  onChange={(e) => setFormData({ ...formData, start_month: parseInt(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(2025, i, 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">End Month</label>
                <select
                  value={formData.end_month}
                  onChange={(e) => setFormData({ ...formData, end_month: parseInt(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(2025, i, 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows={3}
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">Active</label>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? <LoadingSpinner size="sm" /> : (initialData ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GrowthSettings;
